import axiosInstance from "../../utils/axiosConfig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../components/data-table";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import {
  buildCustomerRows,
  createCustomerColumns,
  customerKycLevel,
  CUSTOMERS_TABLE_LIMIT,
  type CustomerRecord,
  type CustomerRow,
} from "./customersTable";
import { formatOrdinalDateTime } from "./clientsTable";

const Customers = ({ setKycLevel, setDate, setCustomerModal }: any) => {
  const { isAuthenticated, setCustomerId, query } = useAuth();
  const [page, setPage] = useState<number>(1);
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);

    const fetchCustomers = () => {
      const searchParam = query ? `&search=${query}` : "";
      axiosInstance
        .get(
          `${API_URL}api/users/?limit=${CUSTOMERS_TABLE_LIMIT}&page=${page}${searchParam}`
        )
        .then((r: any) => {
          setTotalPages(Math.max(1, r.data.data.meta.totalPages ?? 1));
          setTotalUsers(r.data.data.meta.totalUsers ?? 0);
          setCustomers(
            Array.isArray(r.data.data.users) ? r.data.data.users : []
          );
        })
        .catch((e: any) => {
          console.log(e?.response?.data?.message || "An error occurred");
        })
        .finally(() => setLoading(false));
    };

    const debounceTimer = setTimeout(fetchCustomers, 600);
    return () => clearTimeout(debounceTimer);
  }, [page, isAuthenticated, query]);

  const openCustomer = useCallback(
    (row: CustomerRow) => {
      const customer = row.raw;
      setCustomerModal((prev: any) => !prev);
      setCustomerId(customer._id);
      setDate(formatOrdinalDateTime(customer.lastLogin));
      setKycLevel(customerKycLevel(customer));
    },
    [setCustomerModal, setCustomerId, setDate, setKycLevel]
  );

  const columns = useMemo(
    () => createCustomerColumns(openCustomer),
    [openCustomer]
  );
  const rows = useMemo<CustomerRow[]>(
    () => buildCustomerRows(customers),
    [customers]
  );

  if (loading && customers.length === 0) {
    return (
      <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  return (
    <div className="w-full pb-10">
      {loading && (
        <p className="mb-2 text-[12px] text-[#869AA9]">Updating list…</p>
      )}
      <DataTable<CustomerRow>
        columns={columns}
        data={rows}
        currentPage={page}
        setCurrentPage={setPage}
        totalPage={totalPages}
        count={totalUsers}
        limit={CUSTOMERS_TABLE_LIMIT}
        onRowClick={openCustomer}
        emptyContent="No customers to display."
        className="overflow-hidden rounded-xl border border-[#2C363E]"
        fixedBodyHeight="min(560px, 55vh)"
      />
    </div>
  );
};

export default Customers;
